import React, { useState, useRef } from 'react';
import * as XLSX from 'xlsx';
import {
  X,
  UploadCloud,
  FileSpreadsheet,
  Download,
  CheckCircle2,
  AlertTriangle,
  XCircle,
  HelpCircle,
  RefreshCw,
  Info,
  ChevronLeft,
  ChevronRight,
  ArrowRight,
  Sparkles,
} from 'lucide-react';
import { Siswa, Kelas } from '../../types/index.js';
import { api } from '../../services/api.js';

interface ImportSiswaModalProps {
  isOpen: boolean;
  onClose: () => void;
  kelasList: Kelas[];
  siswaList: Siswa[];
  onSuccess: () => Promise<void> | void;
}

interface ParsedStudentRow {
  rowNum: number;
  nis: string;
  nisn?: string;
  nama: string;
  kelasId: string;
  kelasNama: string;
  thnAngkatan: string;
  noVA: string;
  mobile: string;
  namaWali: string;
  kategori: string;
  status: string;
  statusType: 'new' | 'update' | 'warning' | 'error';
  statusMsg: string;
}

export const ImportSiswaModal: React.FC<ImportSiswaModalProps> = ({
  isOpen,
  onClose,
  kelasList,
  siswaList,
  onSuccess,
}) => {
  const [file, setFile] = useState<File | null>(null);
  const [parsedRows, setParsedRows] = useState<ParsedStudentRow[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [filterTab, setFilterTab] = useState<'all' | 'new' | 'update' | 'problem'>('all');
  const [updateExisting, setUpdateExisting] = useState(true);
  const [autoGenerateVA, setAutoGenerateVA] = useState(true);
  const [previewPage, setPreviewPage] = useState(1);
  const [pageSize] = useState(10);
  const [importResult, setImportResult] = useState<{
    success: boolean;
    message: string;
    inserted: number;
    updated: number;
    skipped: number;
    errors: Array<{ row: number; nis?: string; nama?: string; message: string }>;
  } | null>(null);

  const fileInputRef = useRef<HTMLInputElement>(null);

  if (!isOpen) return null;

  // Set existing NIS map untuk validasi cepat
  const existingNisMap = new Map<string, Siswa>();
  siswaList.forEach((s) => {
    if (s.nis) existingNisMap.set(String(s.nis).trim(), s);
  });

  // Normalisasi kelas lookup
  const kelasMap = new Map<string, Kelas>();
  kelasList.forEach((k) => {
    kelasMap.set(k.nama.trim().toLowerCase(), k);
    kelasMap.set(k.nama.replace(/\s+/g, '').toLowerCase(), k);
    kelasMap.set(k.id.trim().toLowerCase(), k);
  });

  // 1. Download Template Excel (.xlsx)
  const handleDownloadTemplate = () => {
    const wb = XLSX.utils.book_new();

    // Sheet 1: Data Siswa (Template Pengisian)
    const headers = [
      'NIS',
      'Nama Siswa',
      'Kelas',
      'Tahun Angkatan',
      'No VA',
      'No HP / WhatsApp',
      'Nama Wali',
      'Kategori',
      'Status',
    ];

    const contohData = [
      [
        '2026001',
        'MUHAMMAD AL FATIH',
        kelasList[0]?.nama || '1 AL BAYAN',
        '2026',
        '',
        '081234567890',
        'Ahmad Fauzi',
        'REGULER',
        'Aktif',
      ],
      [
        '2026002',
        'AISYAH AZ ZAHRA',
        kelasList[0]?.nama || '1 AL BAYAN',
        '2026',
        '',
        '081298765432',
        'Bambang Prasetyo',
        'BEASISWA',
        'Aktif',
      ],
      [
        '2026003',
        'IBRAHIM KHALILULLAH',
        kelasList[1]?.nama || kelasList[0]?.nama || '1 AL FURQON',
        '2026',
        '89986500002026003',
        '081311223344',
        'Nur Hidayat',
        'REGULER',
        'Aktif',
      ],
    ];

    const wsData = XLSX.utils.aoa_to_sheet([headers, ...contohData]);

    // Set Column Widths agar rapi
    wsData['!cols'] = [
      { wch: 14 }, // NIS
      { wch: 28 }, // Nama Siswa
      { wch: 18 }, // Kelas
      { wch: 16 }, // Tahun Angkatan
      { wch: 22 }, // No VA
      { wch: 20 }, // No HP / WhatsApp
      { wch: 24 }, // Nama Wali
      { wch: 14 }, // Kategori
      { wch: 12 }, // Status
    ];

    XLSX.utils.book_append_sheet(wb, wsData, 'Data Siswa');

    // Sheet 2: Referensi Kelas Aktif
    const refHeaders = ['ID Kelas', 'Nama Kelas', 'Tingkat'];
    const refData = kelasList.map((k) => [k.id, k.nama, k.tingkat]);
    const wsRef = XLSX.utils.aoa_to_sheet([refHeaders, ...refData]);
    wsRef['!cols'] = [{ wch: 18 }, { wch: 24 }, { wch: 10 }];
    XLSX.utils.book_append_sheet(wb, wsRef, 'Daftar Kelas (Referensi)');

    // Sheet 3: Petunjuk & Aturan
    const petunjuk = [
      ['PANDUAN IMPORT EXCEL SISWA & VIRTUAL ACCOUNT (SDMT PONOROGO)'],
      [''],
      ['1. Kolom "NIS" dan "Nama Siswa" WAJIB diisi.'],
      ['2. Kolom "Kelas" harus diisi sesuai nama kelas aktif yang terdaftar (lihat sheet Daftar Kelas).'],
      [
        '3. Kolom "No VA" bersifat opsional. Jika dikosongkan, sistem akan meng-generate nomor VA otomatis dengan format: 899865 + NIS.',
      ],
      ['4. Kolom "Tahun Angkatan" jika kosong akan otomatis diisi tahun 2026.'],
      ['5. Kolom "Kategori" pilihan yang valid: REGULER, BEASISWA, YATIM, PRESTASI (default: REGULER).'],
      ['6. Kolom "Status" pilihan yang valid: Aktif, Lulus, Pindah, Keluar (default: Aktif).'],
      ['7. Format file yang didukung adalah .xlsx atau .xls.'],
      ['8. Jika NIS sudah ada di database, data siswa akan diperbarui jika opsi "Update Data" dicentang.'],
    ];
    const wsPanduan = XLSX.utils.aoa_to_sheet(petunjuk);
    wsPanduan['!cols'] = [{ wch: 80 }];
    XLSX.utils.book_append_sheet(wb, wsPanduan, 'Panduan Pengisian');

    // Download File
    XLSX.writeFile(wb, 'Template_Import_Siswa_SDMT_Ponorogo.xlsx');
  };

  // 2. Parsing File Excel
  const handleFileUpload = (e: React.ChangeEvent<HTMLInputElement>) => {
    const selectedFile = e.target.files?.[0];
    if (!selectedFile) return;
    processExcelFile(selectedFile);
  };

  const handleDrop = (e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    const droppedFile = e.dataTransfer.files?.[0];
    if (!droppedFile) return;
    processExcelFile(droppedFile);
  };

  const processExcelFile = (uploadedFile: File) => {
    setFile(uploadedFile);
    setImportResult(null);
    setPreviewPage(1);

    const reader = new FileReader();
    reader.onload = (evt) => {
      try {
        const bstr = evt.target?.result;
        const wb = XLSX.read(bstr, { type: 'binary' });

        // Ambil sheet pertama (Data Siswa)
        const sheetName = wb.SheetNames[0];
        const ws = wb.Sheets[sheetName];

        // Konversi ke array of objects
        const rawJson: any[] = XLSX.utils.sheet_to_json(ws, { defval: '' });

        if (rawJson.length === 0) {
          alert('File Excel kosong atau tidak memiliki data.');
          setFile(null);
          return;
        }

        const rows: ParsedStudentRow[] = [];

        rawJson.forEach((row, idx) => {
          // Cari kolom secara fleksibel (case-insensitive & whitespace tolerant)
          const getVal = (...keys: string[]) => {
            for (const key of keys) {
              for (const rowKey of Object.keys(row)) {
                if (
                  rowKey.trim().toLowerCase() === key.toLowerCase() ||
                  rowKey.replace(/[^a-zA-Z0-9]/g, '').toLowerCase() === key.replace(/[^a-zA-Z0-9]/g, '').toLowerCase()
                ) {
                  return String(row[rowKey]).trim();
                }
              }
            }
            return '';
          };

          const nis = getVal('nis', 'no induk', 'nomor induk', 'no_induk');
          const nama = getVal('nama siswa', 'nama', 'nama lengkap', 'siswa');
          const kelasRaw = getVal('kelas', 'nama kelas', 'kode kelas', 'id kelas');
          const thnAngkatan = getVal('tahun angkatan', 'thn angkatan', 'angkatan', 'tahun') || '2026';
          const noVA = getVal('no va', 'nomor va', 'va', 'virtual account', 'no_va');
          const mobile = getVal('no hp / whatsapp', 'no hp', 'no wa', 'whatsapp', 'telepon', 'mobile', 'hp');
          const namaWali = getVal('nama wali', 'wali', 'orang tua', 'ortu') || 'Orang Tua / Wali';
          const kategori = getVal('kategori', 'kategori siswa') || 'REGULER';
          const status = getVal('status', 'status siswa') || 'Aktif';

          // Abaikan baris kosong total
          if (!nis && !nama && !kelasRaw) return;

          let statusType: 'new' | 'update' | 'warning' | 'error' = 'new';
          let statusMsg = 'Siap Ditambahkan (Siswa Baru)';

          // Validasi Error: NIS atau Nama kosong
          if (!nis) {
            statusType = 'error';
            statusMsg = 'NIS tidak boleh kosong';
          } else if (!nama) {
            statusType = 'error';
            statusMsg = 'Nama Siswa tidak boleh kosong';
          } else {
            // Cek apakah NIS sudah ada di database
            const existing = existingNisMap.get(nis);
            if (existing) {
              statusType = 'update';
              statusMsg = `Akan memperbarui data siswa: ${existing.nama}`;
            }

            // Cek Kelas
            let matchedKelas: Kelas | undefined;
            if (kelasRaw) {
              matchedKelas =
                kelasMap.get(kelasRaw.toLowerCase()) ||
                kelasMap.get(kelasRaw.replace(/\s+/g, '').toLowerCase());
            }

            if (!matchedKelas) {
              if (statusType !== 'update') {
                statusType = 'warning';
              }
              statusMsg += (statusType === 'update' ? ' • ' : '') + `Kelas "${kelasRaw || 'Kosong'}" tidak ditemukan (akan pakai default)`;
            }
          }

          // Tentukan final kelas
          let finalKelasId = '';
          let finalKelasNama = kelasRaw;
          if (kelasRaw) {
            const k =
              kelasMap.get(kelasRaw.toLowerCase()) ||
              kelasMap.get(kelasRaw.replace(/\s+/g, '').toLowerCase());
            if (k) {
              finalKelasId = k.id;
              finalKelasNama = k.nama;
            }
          }

          rows.push({
            rowNum: idx + 2, // Excel row 1-indexed header + 1
            nis,
            nama: nama.toUpperCase(),
            kelasId: finalKelasId,
            kelasNama: finalKelasNama || (kelasList[0]?.nama || 'Belum Ditentukan'),
            thnAngkatan,
            noVA,
            mobile,
            namaWali,
            kategori: kategori.toUpperCase(),
            status,
            statusType,
            statusMsg,
          });
        });

        setParsedRows(rows);
      } catch (err: any) {
        console.error('Error parsing excel:', err);
        alert('Gagal membaca file Excel. Pastikan file valid (.xlsx / .xls).');
      }
    };

    reader.readAsBinaryString(uploadedFile);
  };

  // Filter Rows
  const filteredRows = parsedRows.filter((r) => {
    if (filterTab === 'new') return r.statusType === 'new';
    if (filterTab === 'update') return r.statusType === 'update';
    if (filterTab === 'problem') return r.statusType === 'warning' || r.statusType === 'error';
    return true;
  });

  const totalPages = Math.ceil(filteredRows.length / pageSize) || 1;
  const paginatedRows = filteredRows.slice((previewPage - 1) * pageSize, previewPage * pageSize);

  const countNew = parsedRows.filter((r) => r.statusType === 'new').length;
  const countUpdate = parsedRows.filter((r) => r.statusType === 'update').length;
  const countWarning = parsedRows.filter((r) => r.statusType === 'warning').length;
  const countError = parsedRows.filter((r) => r.statusType === 'error').length;
  const validRowCount = parsedRows.filter((r) => r.statusType !== 'error').length;

  // 3. Proses Impor ke Backend
  const handleExecuteImport = async () => {
    const validStudents = parsedRows.filter((r) => r.statusType !== 'error');

    if (validStudents.length === 0) {
      alert('Tidak ada data siswa yang valid untuk diimpor.');
      return;
    }

    try {
      setIsLoading(true);
      const res = await api.importSiswaExcel({
        students: validStudents.map((s) => ({
          nis: s.nis,
          nama: s.nama,
          kelasId: s.kelasId,
          kelasNama: s.kelasNama,
          thnAngkatan: s.thnAngkatan,
          noVA: s.noVA,
          mobile: s.mobile,
          namaWali: s.namaWali,
          kategori: s.kategori,
          status: s.status,
        })),
        options: {
          updateExisting,
          autoGenerateVA,
        },
      });

      setImportResult({
        success: true,
        message: res.message,
        inserted: res.data?.inserted || 0,
        updated: res.data?.updated || 0,
        skipped: res.data?.skipped || 0,
        errors: res.data?.errors || [],
      });

      // Panggil onSuccess untuk refresh data
      await onSuccess();
    } catch (err: any) {
      alert('Gagal mengimpor data: ' + (err?.message || 'Terjadi kesalahan server'));
    } finally {
      setIsLoading(false);
    }
  };

  const handleReset = () => {
    setFile(null);
    setParsedRows([]);
    setImportResult(null);
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-slate-900/60 backdrop-blur-xs animate-in fade-in duration-200">
      <div className="bg-white rounded-xl shadow-2xl border border-slate-200 w-full max-w-5xl max-h-[92vh] flex flex-col overflow-hidden">
        {/* Header Modal */}
        <div className="px-6 py-4 border-b border-slate-100 flex items-center justify-between bg-gradient-to-r from-emerald-800 to-teal-800 text-white">
          <div className="flex items-center gap-3">
            <div className="p-2 bg-white/10 rounded-lg backdrop-blur-xs">
              <FileSpreadsheet className="w-5 h-5 text-emerald-300" />
            </div>
            <div>
              <h2 className="text-base font-bold flex items-center gap-2">
                Import Excel (.xlsx) Siswa & VA Massal
                <span className="text-[10px] bg-emerald-400 text-slate-900 font-extrabold px-2 py-0.5 rounded-full uppercase tracking-wider">
                  Siap Pakai
                </span>
              </h2>
              <p className="text-xs text-emerald-100/90">
                SDMT Ponorogo • Unggah data siswa, kontak wali, & buat Virtual Account otomatis
              </p>
            </div>
          </div>
          <button
            onClick={onClose}
            className="text-white/80 hover:text-white p-1.5 rounded-lg hover:bg-white/10 transition cursor-pointer"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        {/* Modal Body */}
        <div className="p-6 overflow-y-auto flex-1 space-y-6">
          {/* Notifikasi Hasil Sukses */}
          {importResult && (
            <div className="bg-emerald-50 border border-emerald-200 rounded-xl p-5 text-emerald-950 animate-in fade-in slide-in-from-top-2">
              <div className="flex items-start gap-3">
                <CheckCircle2 className="w-6 h-6 text-emerald-600 shrink-0 mt-0.5" />
                <div className="flex-1">
                  <h3 className="text-sm font-bold text-emerald-900">Impor Data Siswa Berhasil!</h3>
                  <p className="text-xs text-emerald-800 mt-1">{importResult.message}</p>
                  <div className="mt-3 flex flex-wrap gap-2 text-xs">
                    <span className="bg-emerald-100 text-emerald-900 px-3 py-1 rounded-full font-semibold">
                      ✓ Ditambahkan: {importResult.inserted} Siswa Baru
                    </span>
                    <span className="bg-blue-100 text-blue-900 px-3 py-1 rounded-full font-semibold">
                      ↻ Diperbarui: {importResult.updated} Siswa
                    </span>
                    {importResult.skipped > 0 && (
                      <span className="bg-slate-200 text-slate-700 px-3 py-1 rounded-full font-semibold">
                        ⏭ Dilewati: {importResult.skipped} Siswa
                      </span>
                    )}
                    {importResult.errors.length > 0 && (
                      <span className="bg-rose-100 text-rose-800 px-3 py-1 rounded-full font-semibold">
                        ⚠ Gagal/Error: {importResult.errors.length} Baris
                      </span>
                    )}
                  </div>
                </div>
                <button
                  onClick={onClose}
                  className="bg-emerald-600 hover:bg-emerald-700 text-white text-xs font-semibold px-4 py-2 rounded-lg transition shadow-xs cursor-pointer"
                >
                  Selesai & Tutup
                </button>
              </div>
            </div>
          )}

          {/* Step 1: Download Template Excel */}
          {!importResult && (
            <div className="bg-gradient-to-r from-sky-50 to-indigo-50 border border-sky-100 rounded-xl p-4 flex flex-col md:flex-row items-center justify-between gap-4">
              <div className="flex items-start gap-3">
                <div className="p-2 bg-sky-500/10 text-sky-700 rounded-lg shrink-0">
                  <Info className="w-5 h-5" />
                </div>
                <div>
                  <h4 className="text-xs font-bold text-sky-950 uppercase tracking-wider">
                    Langkah 1: Unduh Format Template Resmi
                  </h4>
                  <p className="text-xs text-slate-600 mt-0.5">
                    Gunakan template resmi SDMT Ponorogo dengan sheet data siswa, sheet daftar kelas aktif, dan panduan pengisian.
                  </p>
                </div>
              </div>
              <button
                type="button"
                onClick={handleDownloadTemplate}
                className="shrink-0 flex items-center gap-2 bg-white hover:bg-sky-50 text-sky-700 border border-sky-200 text-xs font-semibold px-4 py-2.5 rounded-lg shadow-xs transition hover:border-sky-300 cursor-pointer"
              >
                <Download className="w-4 h-4 text-sky-600" />
                <span>Unduh Template .xlsx</span>
              </button>
            </div>
          )}

          {/* Step 2: Upload Area */}
          {!importResult && (
            <div>
              <div className="flex items-center justify-between mb-2">
                <h4 className="text-xs font-bold text-slate-800 uppercase tracking-wider">
                  Langkah 2: Pilih / Drag File Excel (.xlsx / .xls)
                </h4>
                {file && (
                  <button
                    onClick={handleReset}
                    className="text-xs text-rose-600 hover:text-rose-700 font-medium cursor-pointer"
                  >
                    Ganti File
                  </button>
                )}
              </div>

              {!file ? (
                <div
                  onDragOver={(e) => e.preventDefault()}
                  onDrop={handleDrop}
                  onClick={() => fileInputRef.current?.click()}
                  className="border-2 border-dashed border-slate-300 hover:border-emerald-500 hover:bg-emerald-50/20 rounded-xl p-8 text-center cursor-pointer transition flex flex-col items-center justify-center gap-2 group"
                >
                  <div className="p-3 bg-emerald-50 text-emerald-600 rounded-full group-hover:scale-110 transition duration-200">
                    <UploadCloud className="w-8 h-8" />
                  </div>
                  <div>
                    <p className="text-sm font-semibold text-slate-800">
                      Klik untuk memilih file Excel atau seret file ke area ini
                    </p>
                    <p className="text-xs text-slate-500 mt-1">
                      Mendukung format Microsoft Excel (.xlsx, .xls)
                    </p>
                  </div>
                  <input
                    ref={fileInputRef}
                    type="file"
                    accept=".xlsx, .xls"
                    onChange={handleFileUpload}
                    className="hidden"
                  />
                </div>
              ) : (
                <div className="bg-slate-50 border border-slate-200 rounded-xl p-4 flex items-center justify-between">
                  <div className="flex items-center gap-3">
                    <div className="p-2.5 bg-emerald-600 text-white rounded-lg">
                      <FileSpreadsheet className="w-5 h-5" />
                    </div>
                    <div>
                      <p className="text-xs font-bold text-slate-900">{file.name}</p>
                      <p className="text-[11px] text-slate-500">
                        Ukuran: {(file.size / 1024).toFixed(1)} KB • Terbaca {parsedRows.length} baris data
                      </p>
                    </div>
                  </div>
                  <div className="flex items-center gap-2">
                    <span className="text-xs font-semibold bg-emerald-100 text-emerald-800 px-3 py-1 rounded-full">
                      ✓ File Siap Diperiksa
                    </span>
                  </div>
                </div>
              )}
            </div>
          )}

          {/* Step 3: Preview Table & Settings */}
          {parsedRows.length > 0 && !importResult && (
            <div className="space-y-4 pt-2 border-t border-slate-200">
              <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3">
                <div>
                  <h4 className="text-xs font-bold text-slate-800 uppercase tracking-wider">
                    Langkah 3: Pratinjau & Validasi Data
                  </h4>
                  <p className="text-xs text-slate-500 mt-0.5">
                    Periksa kesesuaian data siswa dan nomor Virtual Account sebelum disimpan ke database.
                  </p>
                </div>

                {/* Filter Tabs */}
                <div className="flex items-center gap-1 bg-slate-100 p-1 rounded-lg text-xs font-medium">
                  <button
                    onClick={() => {
                      setFilterTab('all');
                      setPreviewPage(1);
                    }}
                    className={`px-3 py-1 rounded-md transition cursor-pointer ${
                      filterTab === 'all' ? 'bg-white shadow-xs font-semibold text-slate-900' : 'text-slate-600 hover:text-slate-900'
                    }`}
                  >
                    Semua ({parsedRows.length})
                  </button>
                  <button
                    onClick={() => {
                      setFilterTab('new');
                      setPreviewPage(1);
                    }}
                    className={`px-3 py-1 rounded-md transition cursor-pointer ${
                      filterTab === 'new' ? 'bg-emerald-600 text-white font-semibold' : 'text-slate-600 hover:text-slate-900'
                    }`}
                  >
                    Baru ({countNew})
                  </button>
                  <button
                    onClick={() => {
                      setFilterTab('update');
                      setPreviewPage(1);
                    }}
                    className={`px-3 py-1 rounded-md transition cursor-pointer ${
                      filterTab === 'update' ? 'bg-blue-600 text-white font-semibold' : 'text-slate-600 hover:text-slate-900'
                    }`}
                  >
                    Update ({countUpdate})
                  </button>
                  {(countWarning > 0 || countError > 0) && (
                    <button
                      onClick={() => {
                        setFilterTab('problem');
                        setPreviewPage(1);
                      }}
                      className={`px-3 py-1 rounded-md transition cursor-pointer ${
                        filterTab === 'problem' ? 'bg-amber-600 text-white font-semibold' : 'text-amber-700 hover:text-amber-900'
                      }`}
                    >
                      Perhatian ({countWarning + countError})
                    </button>
                  )}
                </div>
              </div>

              {/* Opsi Pengaturan Impor */}
              <div className="bg-slate-50 border border-slate-200 rounded-lg p-3 flex flex-wrap items-center gap-6 text-xs text-slate-700">
                <label className="flex items-center gap-2 cursor-pointer font-medium">
                  <input
                    type="checkbox"
                    checked={updateExisting}
                    onChange={(e) => setUpdateExisting(e.target.checked)}
                    className="w-4 h-4 text-emerald-600 rounded border-slate-300 focus:ring-emerald-500"
                  />
                  <span>Perbarui data jika NIS sudah terdaftar (Upsert Siswa)</span>
                </label>

                <label className="flex items-center gap-2 cursor-pointer font-medium">
                  <input
                    type="checkbox"
                    checked={autoGenerateVA}
                    onChange={(e) => setAutoGenerateVA(e.target.checked)}
                    className="w-4 h-4 text-emerald-600 rounded border-slate-300 focus:ring-emerald-500"
                  />
                  <span>
                    Auto-generate No. VA jika kosong{' '}
                    <span className="text-slate-500 font-normal">(Format: 899865 + NIS)</span>
                  </span>
                </label>
              </div>

              {/* Preview Table */}
              <div className="border border-slate-200 rounded-lg overflow-hidden">
                <div className="overflow-x-auto max-h-72">
                  <table className="w-full text-left text-xs">
                    <thead className="bg-slate-100 text-slate-700 font-semibold sticky top-0 z-10 border-b border-slate-200">
                      <tr>
                        <th className="py-2.5 px-3 w-12 text-center">No</th>
                        <th className="py-2.5 px-3">Status</th>
                        <th className="py-2.5 px-3">NIS</th>
                        <th className="py-2.5 px-3">Nama Siswa</th>
                        <th className="py-2.5 px-3">Kelas</th>
                        <th className="py-2.5 px-3">No. VA</th>
                        <th className="py-2.5 px-3">No. HP / WA</th>
                        <th className="py-2.5 px-3">Wali Murid</th>
                        <th className="py-2.5 px-3">Kategori</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-slate-100 bg-white">
                      {paginatedRows.length === 0 ? (
                        <tr>
                          <td colSpan={9} className="py-8 text-center text-slate-400">
                            Tidak ada data pada tab ini
                          </td>
                        </tr>
                      ) : (
                        paginatedRows.map((r, i) => {
                          const displayVa =
                            r.noVA ||
                            (autoGenerateVA && r.nis ? `8998650000${r.nis}`.slice(0, 16) : '-');

                          return (
                            <tr
                              key={i}
                              className={`hover:bg-slate-50/80 transition ${
                                r.statusType === 'error' ? 'bg-rose-50/40' : ''
                              }`}
                            >
                              <td className="py-2 px-3 text-center text-slate-400 font-mono">
                                {(previewPage - 1) * pageSize + i + 1}
                              </td>
                              <td className="py-2 px-3">
                                {r.statusType === 'new' && (
                                  <span className="inline-flex items-center gap-1 bg-emerald-50 text-emerald-700 border border-emerald-200 text-[10px] font-semibold px-2 py-0.5 rounded-full">
                                    <CheckCircle2 className="w-3 h-3 text-emerald-600" />
                                    Baru
                                  </span>
                                )}
                                {r.statusType === 'update' && (
                                  <span className="inline-flex items-center gap-1 bg-blue-50 text-blue-700 border border-blue-200 text-[10px] font-semibold px-2 py-0.5 rounded-full">
                                    <RefreshCw className="w-3 h-3 text-blue-600" />
                                    Update
                                  </span>
                                )}
                                {r.statusType === 'warning' && (
                                  <span
                                    title={r.statusMsg}
                                    className="inline-flex items-center gap-1 bg-amber-50 text-amber-700 border border-amber-200 text-[10px] font-semibold px-2 py-0.5 rounded-full cursor-help"
                                  >
                                    <AlertTriangle className="w-3 h-3 text-amber-600" />
                                    Kelas Default
                                  </span>
                                )}
                                {r.statusType === 'error' && (
                                  <span
                                    title={r.statusMsg}
                                    className="inline-flex items-center gap-1 bg-rose-50 text-rose-700 border border-rose-200 text-[10px] font-semibold px-2 py-0.5 rounded-full cursor-help"
                                  >
                                    <XCircle className="w-3 h-3 text-rose-600" />
                                    Error
                                  </span>
                                )}
                              </td>
                              <td className="py-2 px-3 font-mono font-medium text-slate-800">
                                {r.nis || <span className="text-rose-500 font-bold">Kosong</span>}
                              </td>
                              <td className="py-2 px-3 font-semibold text-slate-900">
                                {r.nama || <span className="text-rose-500 font-bold">Kosong</span>}
                              </td>
                              <td className="py-2 px-3 text-slate-700">{r.kelasNama}</td>
                              <td className="py-2 px-3 font-mono text-slate-600">
                                {r.noVA ? (
                                  r.noVA
                                ) : (
                                  <span className="text-teal-700 font-medium" title="Otomatis dibuat oleh sistem">
                                    {displayVa}{' '}
                                    <span className="text-[9px] bg-teal-100 text-teal-800 px-1 py-0.2 rounded font-sans">
                                      Auto
                                    </span>
                                  </span>
                                )}
                              </td>
                              <td className="py-2 px-3 text-slate-600">{r.mobile || '-'}</td>
                              <td className="py-2 px-3 text-slate-600">{r.namaWali || '-'}</td>
                              <td className="py-2 px-3">
                                <span className="bg-slate-100 text-slate-700 text-[10px] font-medium px-2 py-0.5 rounded">
                                  {r.kategori}
                                </span>
                              </td>
                            </tr>
                          );
                        })
                      )}
                    </tbody>
                  </table>
                </div>

                {/* Table Footer / Pagination */}
                <div className="bg-slate-50 px-4 py-2 border-t border-slate-200 flex items-center justify-between text-xs text-slate-500">
                  <span>
                    Menampilkan {(previewPage - 1) * pageSize + 1} -{' '}
                    {Math.min(previewPage * pageSize, filteredRows.length)} dari {filteredRows.length} data
                  </span>
                  <div className="flex items-center gap-2">
                    <button
                      onClick={() => setPreviewPage((p) => Math.max(1, p - 1))}
                      disabled={previewPage === 1}
                      className="p-1 rounded border border-slate-200 hover:bg-white disabled:opacity-40 cursor-pointer"
                    >
                      <ChevronLeft className="w-4 h-4" />
                    </button>
                    <span className="font-semibold text-slate-700">
                      Halaman {previewPage} / {totalPages}
                    </span>
                    <button
                      onClick={() => setPreviewPage((p) => Math.min(totalPages, p + 1))}
                      disabled={previewPage === totalPages}
                      className="p-1 rounded border border-slate-200 hover:bg-white disabled:opacity-40 cursor-pointer"
                    >
                      <ChevronRight className="w-4 h-4" />
                    </button>
                  </div>
                </div>
              </div>
            </div>
          )}
        </div>

        {/* Modal Footer */}
        <div className="px-6 py-4 border-t border-slate-200 bg-slate-50 flex items-center justify-between">
          <div className="text-xs text-slate-500">
            {parsedRows.length > 0 && !importResult && (
              <span>
                Total <strong className="text-slate-800">{validRowCount}</strong> siswa valid siap diimpor ke sistem
              </span>
            )}
          </div>
          <div className="flex items-center gap-3">
            <button
              type="button"
              onClick={onClose}
              disabled={isLoading}
              className="px-4 py-2 text-xs font-semibold text-slate-600 hover:text-slate-800 hover:bg-slate-200 rounded-lg transition cursor-pointer"
            >
              {importResult ? 'Tutup' : 'Batal'}
            </button>

            {!importResult && (
              <button
                type="button"
                onClick={handleExecuteImport}
                disabled={isLoading || validRowCount === 0}
                className="flex items-center gap-2 bg-emerald-600 hover:bg-emerald-700 disabled:bg-slate-300 text-white text-xs font-semibold px-5 py-2 rounded-lg transition shadow-xs cursor-pointer disabled:cursor-not-allowed"
              >
                {isLoading ? (
                  <>
                    <RefreshCw className="w-4 h-4 animate-spin" />
                    <span>Sedang Mengimpor Data...</span>
                  </>
                ) : (
                  <>
                    <Sparkles className="w-4 h-4" />
                    <span>Mulai Impor Data ({validRowCount} Siswa)</span>
                  </>
                )}
              </button>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};
